import React from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuPercent, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { Field, TextInput } from '../../../components/form/FormControls';
import useTariffText from '../tariffText';

/**
 * FTE values of a tariff: a default price for the selected values, a
 * percentage raise, and one row per value (0.0 … 1.0) with its own price.
 * `percentInputId` keeps the old uncontrolled raise field (read by id).
 */
const FteValuesEditor = ({ isEdit, tariffValues, defaultPrice, onDefaultPriceChange, onToggle, onPriceChange, onApplyPercentage, percentInputId }) => {
  const { label } = useTariffText();
  return (
    <div className="tw-space-y-4">
      <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">
        {isEdit ? label('modal.fteHintEdit') : label('modal.fteHintAdd')}
      </p>

      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
        <Field label={label('modal.defaultPrice')} htmlFor="fteDefaultPrice" hint={label('modal.defaultPriceHint')}>
          <TextInput id="fteDefaultPrice" type="number" value={defaultPrice} onChange={onDefaultPriceChange} placeholder={label('modal.defaultPricePlaceholder')} className="tw-tabular-nums" />
        </Field>
        <Field label={label('modal.raise')} htmlFor={percentInputId} hint={label('modal.raiseHintFte')}>
          <div className="tw-flex tw-gap-2">
            <TextInput id={percentInputId} type="number" placeholder={label('modal.raisePlaceholder')} className="tw-tabular-nums" />
            <Button onClick={onApplyPercentage} className="tw-shrink-0">
              <LuPercent aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600 dark:tw-text-teal-300" />
              {label('modal.apply')}
            </Button>
          </div>
        </Field>
      </div>

      <ul className="tw-divide-y tw-divide-slate-200/70 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 dark:tw-divide-white/[0.06] dark:tw-border-white/10">
        {tariffValues.map((tariff, index) => {
          const isExisting = tariff.existingId !== null;
          const id = `tariff-${tariff.value}`;
          return (
            <li key={tariff.value} className={`tw-relative tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-px-3 tw-py-2.5 tw-transition-colors ${tariff.selected ? 'tw-bg-teal-500/[0.06]' : 'tw-bg-white/60 dark:tw-bg-white/[0.02]'}`}>
              <input type="checkbox" id={id} checked={tariff.selected} onChange={() => onToggle(index)} className="tw-peer tw-sr-only" />
              <label
                htmlFor={id}
                className="tw-flex tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-items-center tw-gap-2.5 peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-teal-500/50"
              >
                <span aria-hidden="true" className={`tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors ${tariff.selected ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white' : 'tw-border-slate-300 tw-bg-white dark:tw-border-white/20 dark:tw-bg-white/[0.04]'}`}>
                  {tariff.selected && <LuCheck className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />}
                </span>
                <span className="tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">FTE {tariff.value}</span>
                {isExisting && <span className="tw-rounded-full tw-bg-sky-500/10 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-sky-700 dark:tw-text-sky-300">{label('modal.existing')}</span>}
                <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{tariff.selected ? label('modal.selected') : label('modal.notSelected')}</span>
              </label>
              {tariff.selected && (
                <span className="tw-flex tw-items-center tw-gap-2">
                  <TextInput
                    type="number"
                    value={tariff.price}
                    onChange={(e) => onPriceChange(index, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={label('modal.price')}
                    aria-label={label('modal.priceFor', { value: tariff.value })}
                    className="tw-h-9 tw-w-32 tw-tabular-nums"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(index);
                    }}
                    title={label('modal.removeHint')}
                    className="tw-inline-flex tw-h-9 tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2.5 tw-text-xs tw-font-semibold tw-text-rose-600 tw-transition-colors hover:tw-bg-rose-500/10 dark:tw-text-rose-300"
                  >
                    <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    {label('modal.remove')}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('modal.fteClickHint')}</p>
    </div>
  );
};

FteValuesEditor.propTypes = {
  isEdit: PropTypes.bool,
  tariffValues: PropTypes.array.isRequired,
  defaultPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onDefaultPriceChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  onApplyPercentage: PropTypes.func.isRequired,
  percentInputId: PropTypes.string.isRequired,
};

export default FteValuesEditor;
